/**
 * Secret share Mux — links opacos que se queman (1 vista + TTL configurable).
 * Snapshot de playback IDs al crear (no live feed).
 * TTL: segundos o minutos. Selección multi de reels + upload embebido.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { MuxFeedUploader } from "@/components/admin/MuxFeedUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BRAND_MUX_CREATOR_ID,
  BRAND_MUX_EXTERNAL_ID_FEED,
} from "@/lib/brandConfig";
import { useToast } from "@/hooks/use-toast";
import {
  CheckSquare,
  Copy,
  Flame,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Power,
  Square,
  Upload,
} from "lucide-react";
import { isValidMuxPlaybackId } from "@/lib/internationalHomeTemplate";
import { cn } from "@/lib/utils";

export type SecretShareInvite = {
  id: string;
  token: string;
  url: string;
  status: string;
  max_views: number;
  views_count: number;
  snapshot_count: number;
  mux_playback_ids: string[];
  expires_at: string | null;
  is_active: boolean;
  label: string | null;
  recipient: string | null;
  created_at: string;
  consumed_at: string | null;
  max_progress_pct?: number;
  watched_seconds_max?: number;
  watched_seconds_total?: number;
  last_watch_at?: string | null;
};

type TtlUnit = "seconds" | "minutes";

/** Preset value = seconds (always stored as seconds for API). */
const TTL_PRESETS: { label: string; seconds: number }[] = [
  { label: "30 s", seconds: 30 },
  { label: "60 s", seconds: 60 },
  { label: "2 min", seconds: 120 },
  { label: "5 min", seconds: 300 },
  { label: "15 min", seconds: 900 },
  { label: "60 min", seconds: 3600 },
];

type Props = {
  /** Resolved private-reels feed IDs (curated/auto). */
  feedPlaybackIds: string[];
  /** Refresh feed after upload (parent reloads resolved IDs). */
  onFeedRefresh?: () => void | Promise<void>;
};

export function SecretShareMuxSection({
  feedPlaybackIds,
  onFeedRefresh,
}: Props) {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();

  const [invites, setInvites] = useState<SecretShareInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showUploader, setShowUploader] = useState(false);

  const [label, setLabel] = useState("");
  const [recipient, setRecipient] = useState("");
  const [maxViews, setMaxViews] = useState("1");
  const [useViewLimit, setUseViewLimit] = useState(true);
  const [useTimeLimit, setUseTimeLimit] = useState(true);
  const [ttlUnit, setTtlUnit] = useState<TtlUnit>("minutes");
  const [ttlValue, setTtlValue] = useState("60");
  const [ttlPreset, setTtlPreset] = useState<string>("3600"); // seconds key or "custom"
  const [count, setCount] = useState("1");
  /** Ordered selection (feed order when select-all; click order when toggled). */
  const [selectedOrder, setSelectedOrder] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/reel-invitations", { headers });
      const data = (await res.json()) as {
        ok?: boolean;
        invitations?: SecretShareInvite[];
        error?: string;
      };
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setInvites(data.invitations || []);
    } catch (e) {
      toast({
        title: "Error al cargar secret shares",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Drop selections that left the feed
  useEffect(() => {
    const set = new Set(feedPlaybackIds);
    setSelectedOrder((prev) => prev.filter((id) => set.has(id)));
  }, [feedPlaybackIds]);

  const selectedSet = useMemo(() => new Set(selectedOrder), [selectedOrder]);

  const selectedIds = useMemo(
    () => selectedOrder.filter((id) => isValidMuxPlaybackId(id)),
    [selectedOrder],
  );

  const toggleSelect = (id: string) => {
    setSelectedOrder((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const selectAll = () => {
    setSelectedOrder(
      feedPlaybackIds.filter((id) => isValidMuxPlaybackId(id)),
    );
  };

  const clearSelection = () => setSelectedOrder([]);

  const applyTtlPreset = (secondsKey: string) => {
    setTtlPreset(secondsKey);
    if (secondsKey === "custom") return;
    const secs = Number(secondsKey);
    if (secs < 60) {
      setTtlUnit("seconds");
      setTtlValue(String(secs));
    } else {
      setTtlUnit("minutes");
      setTtlValue(String(Math.round(secs / 60)));
    }
  };

  const resolveExpiresPayload = (): {
    expires_in_seconds?: number;
    expires_in_minutes?: number;
  } => {
    if (!useTimeLimit) return {};
    if (ttlUnit === "seconds") {
      const secs = Math.max(15, Math.floor(Number(ttlValue) || 30));
      return { expires_in_seconds: secs };
    }
    const mins = Math.max(1, Math.floor(Number(ttlValue) || 60));
    return { expires_in_minutes: mins };
  };

  const create = async () => {
    if (feedPlaybackIds.length === 0) {
      toast({
        title: "Sin videos",
        description: "Subí al menos un reel antes de crear el link.",
        variant: "destructive",
      });
      return;
    }
    setCreating(true);
    try {
      const headers = await getAuthHeaders();
      const body: Record<string, unknown> = {
        access_mode: "feed",
        snapshot: true,
        label: label.trim() || null,
        recipient: recipient.trim() || null,
        count: Math.min(50, Math.max(1, Number(count) || 1)),
        ...resolveExpiresPayload(),
      };

      if (useViewLimit) {
        body.max_views = Math.max(1, Number(maxViews) || 1);
      } else {
        body.max_views = null;
      }

      // Explicit selection freezes those videos; empty = full private-reels resolve
      if (selectedIds.length > 0) {
        body.mux_playback_ids = selectedIds;
      }

      const res = await fetch("/api/admin/reel-invitations", {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        invitations?: SecretShareInvite[];
        error?: string;
        snapshot_count?: number;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const created = data.invitations || [];
      setInvites((prev) => [...created, ...prev]);
      const urls = created.map((i) => i.url).join("\n");
      try {
        await navigator.clipboard.writeText(urls);
        toast({
          title: `${created.length} secret share(s) creado(s)`,
          description: `Snapshot ${data.snapshot_count ?? "?"} video(s). URLs copiadas.`,
        });
      } catch {
        toast({
          title: `${created.length} creado(s)`,
          description: "Copiá las URLs desde la tabla",
        });
      }
    } catch (e) {
      toast({
        title: "No se pudo crear",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const postAction = async (
    action: "set_active" | "reset_views",
    id: string,
    extra?: Record<string, unknown>,
  ) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/reel-invitations", {
        method: "POST",
        headers,
        body: JSON.stringify({ action, id, ...extra }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        invitation?: SecretShareInvite;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.invitation) {
        throw new Error(data.error || "Error");
      }
      setInvites((prev) =>
        prev.map((i) => (i.id === id ? { ...i, ...data.invitation! } : i)),
      );
      toast({
        title: action === "reset_views" ? "Vistas reseteadas" : "Actualizado",
      });
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    }
  };

  const onUploadReady = useCallback(
    async (playbackId: string) => {
      if (!isValidMuxPlaybackId(playbackId)) {
        toast({
          title: "Playback ID inválido",
          variant: "destructive",
        });
        return;
      }
      // Append to curated private-reels playlist so the picker can show it
      try {
        const headers = await getAuthHeaders();
        const getRes = await fetch("/api/admin/private-reels?light=1", {
          headers,
        });
        const getData = (await getRes.json()) as {
          ok?: boolean;
          config?: {
            enabled: boolean;
            access_mode: string;
            curated_playback_ids: string[];
            max_videos_in_feed: number;
            max_views_per_session: number;
          };
          error?: string;
        };
        if (!getRes.ok || !getData.ok || !getData.config) {
          throw new Error(getData.error || "No se pudo leer config de reels");
        }
        const cfg = getData.config;
        const prev = Array.isArray(cfg.curated_playback_ids)
          ? cfg.curated_playback_ids
          : [];
        const nextIds = prev.includes(playbackId)
          ? prev
          : [...prev, playbackId];
        const putRes = await fetch("/api/admin/private-reels", {
          method: "PUT",
          headers,
          body: JSON.stringify({
            enabled: cfg.enabled,
            access_mode: cfg.access_mode,
            playback_source: "curated",
            curated_playback_ids: nextIds,
            max_videos_in_feed: cfg.max_videos_in_feed,
            max_views_per_session: cfg.max_views_per_session,
          }),
        });
        const putData = (await putRes.json()) as {
          ok?: boolean;
          error?: string;
        };
        if (!putRes.ok || !putData.ok) {
          throw new Error(putData.error || `HTTP ${putRes.status}`);
        }
        await onFeedRefresh?.();
        setSelectedOrder((prev) =>
          prev.includes(playbackId) ? prev : [...prev, playbackId],
        );
        toast({
          title: "Reel listo y seleccionado",
          description: "Quedó en la playlist curada y marcado para el share.",
        });
      } catch (e) {
        toast({
          title: "Video en Mux, pero no en playlist",
          description:
            e instanceof Error
              ? e.message
              : "Agregalo manualmente en Videos.",
          variant: "destructive",
        });
      }
    },
    [getAuthHeaders, onFeedRefresh, toast],
  );

  const snapshotLabel =
    selectedIds.length > 0
      ? `${selectedIds.length} video${selectedIds.length === 1 ? "" : "s"} seleccionado${selectedIds.length === 1 ? "" : "s"}`
      : feedPlaybackIds.length > 0
        ? `todos los del feed (${feedPlaybackIds.length})`
        : "sin videos";

  return (
    <section className="rounded-2xl border border-amber-500/25 bg-zinc-900/50 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Flame className="h-4 w-4 text-amber-400" /> Secret share — elegí
            reels + link que expira
          </h2>
          <p className="mt-1 text-[11px] text-muted-foreground max-w-2xl">
            Token opaco →{" "}
            <code className="text-[10px]">/reels/:token</code>. Congela un{" "}
            <strong className="text-white/70">snapshot</strong> de los videos
            elegidos. TTL en{" "}
            <strong className="text-white/70">segundos o minutos</strong>.
            Default: 1 vista + 60 min. Preview de WhatsApp/IG{" "}
            <strong className="text-white/70">no gasta la vista</strong> (anti-unfurl).
          </p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="font-mono text-[10px]">
          creator_id={BRAND_MUX_CREATOR_ID}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          external_id={BRAND_MUX_EXTERNAL_ID_FEED}
        </Badge>
        <Badge variant="outline" className="text-[10px]">
          snapshot only
        </Badge>
      </div>

      {/* Upload reel */}
      <div className="rounded-xl border border-white/[0.08] bg-black/25 p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Upload className="h-4 w-4 text-violet-300" />
            Subir reel
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setShowUploader((v) => !v)}
            >
              {showUploader ? "Ocultar uploader" : "Mostrar uploader"}
            </Button>
            <Button type="button" size="sm" variant="outline" asChild>
              <Link to="/admin/reels/videos">Ir a Videos</Link>
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Cada upload se agrega a la playlist curada y aparece abajo para
          seleccionar en el share.
        </p>
        {showUploader && (
          <MuxFeedUploader
            deferList
            onReady={(id) => void onUploadReady(id)}
          />
        )}
      </div>

      {/* Meta form */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Etiqueta</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="press / cliente VIP"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Destinatario (opcional)</Label>
          <Input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="nombre o email interno"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Cantidad de links (1–50)</Label>
          <Input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
      </div>

      {/* Limits */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-white/[0.06] bg-black/20 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Límite por vistas</Label>
            <Switch checked={useViewLimit} onCheckedChange={setUseViewLimit} />
          </div>
          <Input
            type="number"
            min={1}
            disabled={!useViewLimit}
            value={maxViews}
            onChange={(e) => setMaxViews(e.target.value)}
          />
          <p className="text-[10px] text-muted-foreground">
            Default 1 = se quema en la primera apertura.
          </p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-black/20 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Límite por tiempo</Label>
            <Switch checked={useTimeLimit} onCheckedChange={setUseTimeLimit} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select
              value={ttlPreset}
              onValueChange={applyTtlPreset}
              disabled={!useTimeLimit}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Preset" />
              </SelectTrigger>
              <SelectContent>
                {TTL_PRESETS.map((p) => (
                  <SelectItem key={p.seconds} value={String(p.seconds)}>
                    {p.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={ttlUnit}
              onValueChange={(v) => {
                setTtlUnit(v as TtlUnit);
                setTtlPreset("custom");
              }}
              disabled={!useTimeLimit}
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="seconds">Segundos</SelectItem>
                <SelectItem value="minutes">Minutos</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={ttlUnit === "seconds" ? 15 : 1}
              disabled={!useTimeLimit}
              className="w-24"
              value={ttlValue}
              onChange={(e) => {
                setTtlValue(e.target.value);
                setTtlPreset("custom");
              }}
            />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Mín. 15 s. Si desactivás vistas y tiempo, el server rechaza.
          </p>
        </div>
      </div>

      {/* Video multi-select */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>
            Videos del snapshot{" "}
            <span className="text-muted-foreground font-normal">
              ({snapshotLabel})
            </span>
          </Label>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={feedPlaybackIds.length === 0}
              onClick={selectAll}
            >
              <CheckSquare className="h-3.5 w-3.5 mr-1" /> Todos
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={selectedOrder.length === 0}
              onClick={clearSelection}
            >
              <Square className="h-3.5 w-3.5 mr-1" /> Ninguno
            </Button>
          </div>
        </div>
        {feedPlaybackIds.length === 0 ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-950/30 px-3 py-3 space-y-2">
            <p className="text-[11px] text-amber-200/90">
              La playlist de reels está vacía. Subí un reel con el uploader de
              arriba (o en{" "}
              <Link className="underline" to="/admin/reels/videos">
                Videos
              </Link>
              ) y después creá el share.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-2 max-h-56 overflow-y-auto">
            {feedPlaybackIds.map((id) => {
              if (!isValidMuxPlaybackId(id)) return null;
              const on = selectedSet.has(id);
              const ord = on ? selectedOrder.indexOf(id) + 1 : 0;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleSelect(id)}
                  className={cn(
                    "relative overflow-hidden rounded-lg border transition",
                    on
                      ? "border-amber-400/70 ring-1 ring-amber-400/40"
                      : "border-white/10 opacity-80 hover:opacity-100",
                  )}
                  title={id}
                >
                  <img
                    src={`https://image.mux.com/${id}/thumbnail.jpg?width=120&height=160&fit_mode=smartcrop&time=1`}
                    alt=""
                    className="aspect-[3/4] w-full object-cover"
                  />
                  {on && (
                    <span className="absolute inset-x-0 bottom-0 bg-amber-500/90 text-[9px] font-medium text-black text-center py-0.5">
                      #{ord}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">
          Sin selección = snapshot de todo el feed actual de reels privados
          (curado). Con selección = solo esos videos, en el orden marcado.
        </p>
      </div>

      <Button
        type="button"
        onClick={() => void create()}
        disabled={creating || feedPlaybackIds.length === 0}
        className="bg-amber-500/90 text-black hover:bg-amber-400"
      >
        {creating ? (
          <Loader2 className="h-4 w-4 animate-spin mr-1" />
        ) : (
          <Plus className="h-4 w-4 mr-1" />
        )}
        Crear secret share
        {Number(count) > 1 ? ` ×${count}` : ""}
      </Button>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
        <table className="w-full text-left text-[11px]">
          <thead className="bg-white/[0.03] text-muted-foreground">
            <tr>
              <th className="px-2 py-2 font-medium">Estado</th>
              <th className="px-2 py-2 font-medium">Etiqueta</th>
              <th className="px-2 py-2 font-medium">Aperturas</th>
              <th className="px-2 py-2 font-medium">Visto</th>
              <th className="px-2 py-2 font-medium">Snapshot</th>
              <th className="px-2 py-2 font-medium">Expira</th>
              <th className="px-2 py-2 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && invites.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-muted-foreground">
                  Sin secret shares todavía.
                </td>
              </tr>
            )}
            {invites.map((inv) => (
              <tr key={inv.id} className="border-t border-white/[0.05]">
                <td className="px-2 py-2">
                  <Badge
                    variant={inv.status === "active" ? "default" : "outline"}
                    className="text-[9px]"
                  >
                    {inv.status}
                  </Badge>
                </td>
                <td className="px-2 py-2">
                  <div className="font-medium">{inv.label || "—"}</div>
                  <div className="max-w-[200px] truncate font-mono text-[9px] text-muted-foreground">
                    {inv.url}
                  </div>
                </td>
                <td className="px-2 py-2 tabular-nums">
                  {inv.views_count}/
                  {inv.max_views >= 1_000_000 ? "∞" : inv.max_views}
                </td>
                <td className="px-2 py-2 tabular-nums text-[10px]">
                  <div>{inv.max_progress_pct ?? 0}% máx</div>
                  <div className="text-muted-foreground">
                    {inv.watched_seconds_max ?? 0}s / total{" "}
                    {inv.watched_seconds_total ?? 0}s
                  </div>
                </td>
                <td className="px-2 py-2 tabular-nums">
                  {inv.snapshot_count ?? inv.mux_playback_ids?.length ?? 0}
                </td>
                <td className="px-2 py-2 text-muted-foreground">
                  {inv.expires_at
                    ? new Date(inv.expires_at).toLocaleString()
                    : "—"}
                </td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(inv.url);
                          toast({ title: "URL copiada" });
                        } catch {
                          toast({
                            title: "No se pudo copiar",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      title={inv.is_active ? "Desactivar" : "Activar"}
                      onClick={() =>
                        void postAction("set_active", inv.id, {
                          is_active: !inv.is_active,
                        })
                      }
                    >
                      <Power className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      title="Reset vistas"
                      onClick={() => void postAction("reset_views", inv.id)}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
